/**
 * Styled components for SideNav: wrapper, items container, toggle handle,
 * expanded panel, resize handle, and panel control button.
 */
import styled from "styled-components";
import { css } from "@styled-system/css";
import { themeGet } from "@styled-system/theme-get";
import Popover from "../../Popover";
import { EXPANDED_SIZE, SIDENAV_WIDTHS } from "../constants/sideNav";
import { applySmallScreenObject } from "./smallScreen";
import { HandleTrack } from "./paneEdgeHandle.styles";

const createShouldForwardProp = (blockedProps) =>
  function shouldForwardProp(prop, defaultValidatorFn) {
    if (blockedProps.includes(prop)) {
      return false;
    }

    if (typeof defaultValidatorFn === "function") {
      return defaultValidatorFn(prop);
    }

    return true;
  };

const filterIsExpandedProp = createShouldForwardProp(["isExpanded"]);

export const SideNavWrapper = styled("div").withConfig({
  shouldForwardProp: createShouldForwardProp(["sideNavHeight"]),
})((props) =>
  css({
    bg: themeGet("colors.surface")(props),
    color: themeGet("colors.bodyText")(props),
    minHeight: props.sideNavHeight || "100vh",
    height: "100%",
    maxWidth: "max-content",
    fontFamily: themeGet("fonts.main")(props),
    borderRight: `solid 1px ${themeGet("colors.surfaceBorder")(props)}`,
    display: "flex",
    alignItems: "stretch",
    position: "relative",
    alignContent: "stretch",
    zIndex: "5",
    flexShrink: "0",
    alignSelf: "stretch",
    ...applySmallScreenObject(props, {
      borderRight: "none",
      borderTop: `solid 1px ${themeGet("colors.surfaceBorder")(props)}`,
      width: "100%",
      height: "auto",
      minWidth: "auto",
      maxWidth: "initial",
      minHeight: "initial",
      maxHeight: themeGet("appScale.sidebarMobileHeight", "100vh")(props),
      position: "fixed",
      bottom: "0",
      left: "0",
      zIndex: "1000",
      flexDirection: "column-reverse",
    }),
  })
);

export const SideNavItems = styled("div").withConfig({
  shouldForwardProp: filterIsExpandedProp,
})((props) =>
  css({
    width: props.isExpanded
      ? SIDENAV_WIDTHS.EXPANDED
      : themeGet("appScale.sideNavSize")(props),
    height: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    flexShrink: "0",
    flexGrow: "1",
    padding: "10px 10px 10px 10px",
    gap: "4px",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "center",
    position: "relative",
    minHeight: "100%",

    // Expanded state overrides
    ...(props.isExpanded && {
      padding: "10px 9px 10px 9px",
      alignItems: "stretch",
      textAlign: "left",
    }),

    ...applySmallScreenObject(props, {
      padding: "10px",
      height: "auto",
      flexDirection: "row",
      justifyContent: "space-around",
      alignItems: "center",
      width: "auto",
      zIndex: 0,
      position: "relative",
    }),
  })
);

export const PanelControlTooltip = styled(Popover)`
  ${(props) =>
    (props.position === "absolute" || props.position === "sticky") &&
    css({
      // Sticky inside SideNavExpanded (overflowY:auto) so the X stays visible
      // while Teams/Goals/etc. scroll underneath. Nudge up so it clears the
      // title/Divider under Explore panel headers.
      position: "sticky",
      float: "right",
      top: "2px",
      marginTop: "-6px",
      zIndex: 2,
    })}
`;

export const PanelControl = styled("button")((props) =>
  css({
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    background: themeGet("colors.hoverSurface")(props),
    color: themeGet("colors.subText")(props),
    fontSize: "1.2rem",
    appearance: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    transition: themeGet("transition.transitionDefault")(props),
    "&:hover, &:focus": {
      outline: "none",
      backgroundColor: themeGet("colors.primaryLightest")(props),
    },
    "&:focus-visible": {
      outline: `2px solid ${themeGet("colors.linkText")(props)}`,
      outlineOffset: "2px",
    },
  })
);

export const ToggleHandle = styled(HandleTrack).withConfig({
  shouldForwardProp: filterIsExpandedProp,
})((props) =>
  css({
    left: props.isExpanded
      ? `calc(${SIDENAV_WIDTHS.EXPANDED} - 8px)`
      : `calc(${themeGet("appScale.sideNavSize")(props)} - 8px)`,
    right: "auto",
    zIndex: "100",
  })
);

export const SideNavExpanded = styled("div")`
  /* Inherited by TreeViewNext for half-gutter hover bleed. space.l = 16px. */
  --side-nav-expanded-padding-x: ${(props) => themeGet("space.l")(props)};
  /* Disable TreeViewNext's legacy inline pad inside the expanded panel. */
  --tree-view-inline-pad: 0px;
  ${(props) =>
    css({
      position: "relative",
      backgroundColor: themeGet("colors.surface")(props),
      display: props.active ? "block" : "none",
      width: props.width ? `${props.width}px` : "auto",
      minWidth: props.large
        ? `${EXPANDED_SIZE.largeMin}px`
        : `${EXPANDED_SIZE.normalMin}px`,
      maxWidth: props.large
        ? `${EXPANDED_SIZE.largeMax}px`
        : `${EXPANDED_SIZE.normalMax}px`,
      height: "inherit", // Always inherit height on desktop
      overflowX: "hidden",
      overflowY: "auto",
      paddingTop: "10px",
      paddingBottom: themeGet("space.l")(props),
      paddingLeft: "var(--side-nav-expanded-padding-x)",
      paddingRight: "var(--side-nav-expanded-padding-x)",
      borderLeft: `solid 1px ${themeGet("colors.surfaceBorder")(props)}`,
      zIndex: 1,
      "&:focus": {
        outline: "0",
      },
      ...applySmallScreenObject(props, {
        width: "100vw",
        minWidth: "initial",
        maxWidth: "initial",
        borderLeft: "none",
        borderTop: `solid 1px ${themeGet("colors.surfaceBorder")(props)}`,
        borderBottom: `solid 1px ${themeGet("colors.surfaceBorder")(props)}`,
        height: props.height ? `${props.height}px` : "auto",
        minHeight: props.large
          ? `${EXPANDED_SIZE.largeMin}px`
          : `${EXPANDED_SIZE.normalMin}px`,
        maxHeight: props.large
          ? `${EXPANDED_SIZE.largeMax}px`
          : `${EXPANDED_SIZE.normalMax}px`,
        zIndex: 1001,
        position: "absolute",
        bottom: "100%",
        left: "0",
        right: "0",
      }),
    })}
`;
